import { Header } from "../components/Header";
import { KpiCard } from "../components/KpiCard";
import { SyncStatus } from "../components/SyncStatus";
import { CampaignTable } from "../components/CampaignTable";
import { MetaCampaignTable } from "../components/MetaCampaignTable";
import { TikTokCampaignTable } from "../components/TikTokCampaignTable";
import { SeoPagesTable } from "../components/SeoPagesTable";
import {
  useGoogleAdsData,
  aggregateKpis,
  aggregateByCampaign,
} from "../lib/google-ads";
import {
  useMetaAdsData,
  aggregateMetaKpis,
  aggregateMetaByCampaign,
} from "../lib/meta-ads";
import {
  useTikTokAdsData,
  aggregateTikTokKpis,
  aggregateTikTokByCampaign,
} from "../lib/tiktok-ads";
import { useSeoData, summarisePages } from "../lib/seo";
import { formatCurrency, formatInt, formatMicros, formatPercent } from "../lib/format";

const env = (import.meta.env.MODE ?? "development") as string;
const branch = import.meta.env.VITE_GIT_BRANCH ?? "local";

export function Dashboard() {
  return (
    <>
      <Header />
      <main className="dashboard">
        <section className="intro">
          <h1>Dashboard</h1>
          <p className="meta">
            env: <code>{env}</code> · branch: <code>{branch}</code>
          </p>
        </section>

        <GoogleAdsSection />
        <MetaAdsSection />
        <TikTokAdsSection />
        <SeoSection />
      </main>
    </>
  );
}

function GoogleAdsSection() {
  const { account, campaigns, metrics, syncRuns, loading, error } = useGoogleAdsData();
  const kpis = aggregateKpis(metrics);
  const byCampaign = aggregateByCampaign(campaigns, metrics);
  const currency = account?.currency_code ?? "AUD";

  return (
    <section className="channel-section">
      <header className="channel-header">
        <h2>Google Ads</h2>
        {account && (
          <span className="channel-meta">
            <code>{account.descriptive_name ?? account.id}</code> · {currency}
          </span>
        )}
      </header>

      <SyncStatus runs={syncRuns} label="Google Ads" />

      {error && (
        <div className="error-panel" role="alert">
          <strong>Couldn't load Google Ads data:</strong> <code>{error}</code>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="Impressions (30d)" value={loading ? "…" : formatInt(kpis.impressions)} />
        <KpiCard
          label="Clicks (30d)"
          value={loading ? "…" : formatInt(kpis.clicks)}
          hint={loading ? undefined : `CTR ${formatPercent(kpis.ctr)}`}
        />
        <KpiCard
          label="Cost (30d)"
          value={loading ? "…" : formatMicros(kpis.costMicros, currency)}
          hint={loading ? undefined : `avg CPC ${formatMicros(kpis.avgCpcMicros, currency)}`}
        />
        <KpiCard
          label="Conversions (30d)"
          value={loading ? "…" : kpis.conversions.toFixed(kpis.conversions < 10 ? 2 : 0)}
        />
      </div>

      <CampaignTable rows={byCampaign} currency={currency} />
    </section>
  );
}

function MetaAdsSection() {
  const { account, campaigns, metrics, syncRuns, loading, error } = useMetaAdsData();
  const kpis = aggregateMetaKpis(metrics);
  const byCampaign = aggregateMetaByCampaign(campaigns, metrics);
  const currency = account?.currency ?? "AUD";

  return (
    <section className="channel-section">
      <header className="channel-header">
        <h2>Meta Ads</h2>
        {account && (
          <span className="channel-meta">
            <code>{account.name ?? account.id}</code> · {currency}
          </span>
        )}
      </header>

      <SyncStatus runs={syncRuns} label="Meta Ads" />

      {error && (
        <div className="error-panel" role="alert">
          <strong>Couldn't load Meta Ads data:</strong> <code>{error}</code>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="Impressions (30d)" value={loading ? "…" : formatInt(kpis.impressions)} />
        <KpiCard
          label="Clicks (30d)"
          value={loading ? "…" : formatInt(kpis.clicks)}
          hint={loading ? undefined : `CTR ${formatPercent(kpis.ctr)}`}
        />
        <KpiCard
          label="Spend (30d)"
          value={loading ? "…" : formatCurrency(kpis.spend, currency)}
          hint={loading ? undefined : `avg CPC ${formatCurrency(kpis.avgCpc, currency)}`}
        />
        <KpiCard
          label="Conversions (30d)"
          value={loading ? "…" : kpis.conversions.toFixed(kpis.conversions < 10 ? 2 : 0)}
        />
      </div>

      <MetaCampaignTable rows={byCampaign} currency={currency} />
    </section>
  );
}

function TikTokAdsSection() {
  const { advertiser, campaigns, metrics, syncRuns, loading, error } = useTikTokAdsData();
  const kpis = aggregateTikTokKpis(metrics);
  const byCampaign = aggregateTikTokByCampaign(campaigns, metrics);
  const currency = advertiser?.currency ?? "AUD";

  return (
    <section className="channel-section">
      <header className="channel-header">
        <h2>TikTok Ads</h2>
        {advertiser && (
          <span className="channel-meta">
            <code>{advertiser.name ?? advertiser.id}</code> · {currency}
          </span>
        )}
      </header>

      <SyncStatus runs={syncRuns} label="TikTok Ads" />

      {error && (
        <div className="error-panel" role="alert">
          <strong>Couldn't load TikTok Ads data:</strong> <code>{error}</code>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="Impressions (30d)" value={loading ? "…" : formatInt(kpis.impressions)} />
        <KpiCard
          label="Clicks (30d)"
          value={loading ? "…" : formatInt(kpis.clicks)}
          hint={loading ? undefined : `CTR ${formatPercent(kpis.ctr)}`}
        />
        <KpiCard
          label="Spend (30d)"
          value={loading ? "…" : formatCurrency(kpis.spend, currency)}
          hint={loading ? undefined : `avg CPC ${formatCurrency(kpis.avgCpc, currency)}`}
        />
        <KpiCard
          label="Conversions (30d)"
          value={loading ? "…" : kpis.conversions.toFixed(kpis.conversions < 10 ? 2 : 0)}
        />
      </div>

      <TikTokCampaignTable rows={byCampaign} currency={currency} />
    </section>
  );
}

function SeoSection() {
  const { pages, runs, loading, error } = useSeoData();
  const summary = summarisePages(pages);
  const host = pages[0]?.host ?? runs[0]?.target_host ?? "alibi.au";

  return (
    <section className="channel-section">
      <header className="channel-header">
        <h2>SEO</h2>
        <span className="channel-meta">
          <code>{host}</code> · {summary.total} pages
        </span>
      </header>

      <SyncStatus runs={runs} label="SEO crawl" />

      {error && (
        <div className="error-panel" role="alert">
          <strong>Couldn't load SEO data:</strong> <code>{error}</code>
        </div>
      )}

      <div className="kpi-grid">
        <KpiCard label="Pages" value={loading ? "…" : formatInt(summary.total)} />
        <KpiCard
          label="With issues"
          value={loading ? "…" : formatInt(summary.withIssues)}
          hint={
            loading
              ? undefined
              : `${summary.issuesBySeverity.error} errors, ${summary.issuesBySeverity.warning} warnings`
          }
        />
        <KpiCard
          label="Broken pages"
          value={loading ? "…" : formatInt(summary.errors)}
          hint={summary.total > 0 ? `${((summary.errors / summary.total) * 100).toFixed(1)}% of pages` : undefined}
        />
        <KpiCard
          label="Avg response"
          value={loading ? "…" : `${summary.avgResponseMs}ms`}
        />
      </div>

      <SeoPagesTable pages={pages} />
    </section>
  );
}
